import React, { useEffect, useState, useCallback } from 'react';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import { log, LOG_LEVELS } from '../../../../common/config/app_logger';
import ProcessingStatus from '../processing_status/processing_status';
import { purchaseDetailsTypes } from '../../../../common/config/const';
import {
  getFollowupParam, payingWithCreditCard, renderButtonTitle, selectCloseButtonActionIfNeed,
  isAffiliatePromoPopup, isSideMenu, sign, onRetryButtonClick, onConfirmButton,
  mpTrackPurchaseStartedEvent, mpTrackPurchaseEvent, mpTrackPurchaseFailedEvent
} from '../purchases_utils';
import { browserContext, tryParseJSONObject } from '../../../../common/config/utils';
import { useLocalizedNavigate } from '../../routes/hooks/useLocalizedNavigate';

const shouldRenderProcessingStatus = ({
  payPalWindow, errorMessage, okMessage
}) => payPalWindow || errorMessage || okMessage;

const closePayPalWindow = ({ payPalWindow, claerPaypalWindow }) => {
  window.focus();
  if (payPalWindow) {
    payPalWindow.close();
    claerPaypalWindow();
  }
};

const closePurchaseDetailsOnSideMenu = (onPurchaseComplete, type) => {
  if (onPurchaseComplete && isSideMenu({ type })) onPurchaseComplete();
};

const closePayPalWindowOnMpTrackPayPalProcessingEventsIfNeed = ({
  okMessage, errorMessage, payPalWindow, claerPaypalWindow
}) => {
  if (okMessage || errorMessage) { closePayPalWindow({ payPalWindow, claerPaypalWindow }); }
};

const mpTrackPayPalProcessingEvents = ({
  needTrackPurchaseStarted, purchaseResponce, retryCount, type, purchaseAmount, bonus, getUser,
  hasPaypalBillingAgreement, currentPaymentOption, chatType, dispatch, setNeedTrackPurchaseStarted,
  payPalWindow, claerPaypalWindow, purchaseFees, location, advisorId
}) => {
  const {
    errorType, okMessage, errorMessage, errorName, errorCode, gatewayName, errorPathname,
    threeDAuthResult, conversionEventId, extraEventParams
  } = purchaseResponce;
  if (needTrackPurchaseStarted && conversionEventId) {
    mpTrackPurchaseStartedEvent({
      retryCount, conversionEventId, type, purchaseAmount, bonus, hasPaypalBillingAgreement,
      threeDAuthResult, currentPaymentOption, chatType, dispatch, purchaseFees, location, advisorId
    });
    setNeedTrackPurchaseStarted(false);
  }
  if (okMessage) {
    mpTrackPurchaseEvent({
      retryCount, conversionEventId, type, purchaseAmount, bonus, hasPaypalBillingAgreement,
      threeDAuthResult, currentPaymentOption, chatType, dispatch,
      pathname: purchaseResponce.pathname, getUser, extraEventParams, location, advisorId
    });
  }
  if (errorMessage) {
    mpTrackPurchaseFailedEvent({
      errorType, errorMessage, errorName, errorCode, gatewayName, errorPathname, threeDAuthResult,
      retryCount, type, purchaseAmount, bonus, hasPaypalBillingAgreement, currentPaymentOption,
      chatType, dispatch, extraEventParams, location, advisorId
    });
  }
  closePayPalWindowOnMpTrackPayPalProcessingEventsIfNeed({
    okMessage, errorMessage, payPalWindow, claerPaypalWindow
  });
};

const setPurchaseResponceDataIfNeed = ({ e, setPurchaseResponce }) => {
  const parsedPurchaseResponceData = tryParseJSONObject(JSON.stringify(e.data));
  if (parsedPurchaseResponceData) {
    log(LOG_LEVELS.DEBUG, 'Purchase', 'window got message setPurchaseResponceDataIfNeed');
    if (e.origin !== window.location.origin) return;
    setPurchaseResponce(e.data);
  }
};

const focusPayPalWindowIfNeed = ({ payPalWindow }) => { if (payPalWindow) payPalWindow.focus(); };

const openPayPalIfNeed = ({ openPayPal, disabled, purchaseURL }) => {
  if (disabled) return;
  openPayPal(purchaseURL);
};

function PayPalProcessing({
  purchaseURL,
  onPurchaseComplete,
  getUser,
  hasPaypalBillingAgreement = false,
  loadAdvisor,
  type = purchaseDetailsTypes.SIDE_MENU,
  children,
  purchaseAmount,
  t,
  bonus = 0,
  cancelChat,
  userLoading,
  currentPaymentOption,
  isContinue = false,
  disabled = false,
  hidePaymentDetails,
  advisorId,
  cancelChatOnPP,
  chatType,
  hideAffiliatePromoPopup,
  dispatch,
  fees = {},
  notShowPurchaseSuccessPoppup = false
}) {
  const location = useLocation();
  const history = useLocalizedNavigate();
  const [purchaseResponce, setPurchaseResponce] = useState({});
  const [payPalWindow, setPaypalWindow] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [closeWindowEventListenerMessage, setCloseWindowEventListenerMessage] = useState(null);
  const [needTrackPurchaseStarted, setNeedTrackPurchaseStarted] = useState(true);
  const { purchaseFees } = fees;

  const setPurchaseResponceData = (e) => {
    setPurchaseResponceDataIfNeed({ e, setPurchaseResponce });
  };

  const clearPurchaseResponceData = () => { setPurchaseResponce({}); };

  const claerPaypalWindow = () => {
    setPaypalWindow(null);
    if (closeWindowEventListenerMessage) closeWindowEventListenerMessage();
  };

  const openPayPal = (URL) => {
    if (!URL) return;
    const paramsString = queryString.stringify({
      use_reference_transaction: true,
      with3ds: true,
      ...getFollowupParam({ isContinue, type }),
      ...browserContext
    });
    const fullURL = `${ URL }${ sign({ URL }) }${ paramsString }`;
    const {
      frames: { innerHeight, innerWidth },
      top: {
        outerWidth, outerHeight, screenY, screenX
      }
    } = window;
    log(LOG_LEVELS.INFO, 'Purchase', `Opening paypal window with URL:${ fullURL }`);
    const windowTop = outerHeight / 2 + screenY - innerHeight / 2;
    const windowLeft = outerWidth / 2 + screenX - innerWidth / 2;
    const openingwindow = window.open(fullURL, '_blank', `left=${ windowLeft },top=${ windowTop }menubar=false,location=false,toolbar=false,centerscreen=yes,innerWidth=${ innerWidth },innerHeight=${ innerHeight }`);
    setPaypalWindow(openingwindow);
    window.addEventListener('message', setPurchaseResponceData);
    const removeEventListenerMessage = () => { window.removeEventListener('message', setPurchaseResponceData); };
    setCloseWindowEventListenerMessage(() => removeEventListenerMessage);
  };

  useEffect(() => {
    mpTrackPayPalProcessingEvents({
      needTrackPurchaseStarted, purchaseResponce, retryCount, type, purchaseAmount, bonus, getUser,
      hasPaypalBillingAgreement, currentPaymentOption, chatType, dispatch,
      setNeedTrackPurchaseStarted, payPalWindow, claerPaypalWindow, purchaseFees, location, advisorId
    });
  }, [purchaseResponce]);

  let interval = null;
  useEffect(() => {
    if (payPalWindow && !interval) {
      interval = setInterval(() => {
        if (payPalWindow && payPalWindow.closed) {
          log(LOG_LEVELS.INFO, 'Purchase', 'user closed PayPal window');
          mpTrackPurchaseFailedEvent({
            reason: 'user canceled', retryCount, type, purchaseAmount, bonus, hasPaypalBillingAgreement, currentPaymentOption, chatType, dispatch, purchaseFees, location, advisorId
          });
          claerPaypalWindow();
          setNeedTrackPurchaseStarted(true);
        }
      }, 1500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [payPalWindow]);

  const onConfirmButtonClick = () => {
    onConfirmButton({
      clearPurchaseAction: clearPurchaseResponceData, setRetryCount, onPurchaseComplete
    });
  };

  const onFocusWindow = useCallback(() => {
    log(LOG_LEVELS.VERBOSE, 'Purchase', 'on focus window');
    focusPayPalWindowIfNeed({ payPalWindow });
  }, [payPalWindow]);

  const onCloseButtonClick = () => {
    clearPurchaseResponceData();
    claerPaypalWindow();
    setNeedTrackPurchaseStarted(true);
    selectCloseButtonActionIfNeed({ type, cancelChatOnPP, hideAffiliatePromoPopup });
    closePurchaseDetailsOnSideMenu(onPurchaseComplete, type);
  };

  const onRetry = (e) => {
    e.preventDefault();
    setRetryCount(retryCount + 1);
    setNeedTrackPurchaseStarted(true);
    onRetryButtonClick({
      purchaseResponce, hidePaymentDetails, cancelChat, history, loadAdvisor, advisorId,
      purchaseURL, startPurchaseAction: openPayPal,
      clearPurchaseAction: clearPurchaseResponceData, clearAdditionalAction: claerPaypalWindow
    });
  };

  const renderProcessingStatus = () => {
    const { errorMessage, okMessage } = purchaseResponce;
    return (
      <ProcessingStatus
        purchaseResponce={ purchaseResponce }
        t={ t }
        isSideMenu={ isSideMenu({ type }) }
        isAffiliatePromoPopup={ isAffiliatePromoPopup({ type }) }
        onClose={ onCloseButtonClick }
        onFocusProcessingWindow={ onFocusWindow }
        userLoading={ userLoading }
        onRetry={ onRetry }
        onConfirm={ onConfirmButtonClick }
        isCreditCard={ payingWithCreditCard({ currentPaymentOption }) }
        purchaseURL={ purchaseURL }
        buttonTitle={ renderButtonTitle({ isContinue, type, t }) }
        chatType={ chatType }
        purchaseAmount={ purchaseAmount }
        bonus={ bonus }
        visible= { !!shouldRenderProcessingStatus({ payPalWindow, errorMessage, okMessage }) }
        notShowPurchaseSuccessPoppup={ notShowPurchaseSuccessPoppup }
      />
    );
  };

  const onPurchaseClicked = (e) => {
    e.preventDefault();
    openPayPalIfNeed({ openPayPal, disabled, purchaseURL });
  };

  return (
    <>
      <a href={ purchaseURL } target="blank" onClick={ onPurchaseClicked } disabled={ disabled } data-testid="pay-pal-processing-a-container">
        { children }
      </a>
      { renderProcessingStatus() }
    </>
  );
}

export default PayPalProcessing;
